import { ApiResponse } from '../../types';

const apiUrl: string = import.meta.env.VITE_ADMIN_URL;

/**
 * Registra una actividad en el sistema para auditoría
 * 
 * @param token JWT de autenticación
 * @param userId ID del usuario que realiza la acción
 * @param accion Tipo de acción (crear, editar, consultar, eliminar, login, logout, etc.)
 * @param seccion Sección del sistema donde se realiza la acción (empleados, clientes, proyectos, etc.)
 * @param sucursal ID de la sucursal donde se realiza la acción
 * @param detalles Detalles adicionales de la acción (opcional)
 * @returns Promise con el resultado de la operación
 */
export const registrarActividad = (
  token: string, 
  userId: string, 
  accion: string, 
  seccion: string, 
  sucursal: string, 
  detalles = {}
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userId,
        accion,
        seccion,
        sucursal,
        detalles
      }),
    };

    fetch(
      apiUrl + "/auditoria/registrar",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al registrar actividad en auditoría:", err);
        reject(err);
      });
  });
};

/**
 * Registra una actividad de inicio de sesión
 * 
 * @param token JWT de autenticación
 * @param userId ID del usuario
 * @param sucursal ID de la sucursal
 * @param rol Rol del usuario (admin, app, cliente)
 * @returns Promise con el resultado de la operación
 */
export const registrarLogin = (
  token: string,
  userId: string,
  sucursal: string,
  rol: string = 'admin'
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userId,
        sucursal,
        rol
      }),
    };

    fetch(
      apiUrl + "/admin/login",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al registrar login en auditoría:", err);
        reject(err);
      });
  });
};

/**
 * Registra una actividad de cierre de sesión
 * 
 * @param token JWT de autenticación
 * @param userId ID del usuario
 * @param sucursal ID de la sucursal
 * @param rol Rol del usuario (admin, app, cliente)
 * @returns Promise con el resultado de la operación
 */
export const registrarLogout = (
  token: string,
  userId: string,
  sucursal: string,
  rol: string = 'admin'
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userId,
        sucursal,
        rol
      }),
    };

    fetch(
      apiUrl + "/admin/logout",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al registrar logout en auditoría:", err);
        reject(err);
      });
  });
};

/**
 * Obtiene las actividades de auditoría de una sección específica
 * 
 * @param token JWT de autenticación
 * @param sucursal ID de la sucursal
 * @param seccion Sección del sistema
 * @param fechaInicio Fecha de inicio para filtrar (opcional)
 * @param fechaFin Fecha de fin para filtrar (opcional)
 * @param limite Número máximo de registros a devolver (opcional)
 * @returns Promise con el resultado de la operación
 */
export const obtenerActividadesPorSeccion = (
  token: string,
  sucursal: string,
  seccion: string,
  fechaInicio?: string,
  fechaFin?: string,
  limite?: number
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    let url = `${apiUrl}/admin/auditoria/seccion/${sucursal}/${seccion}`;
    
    // Agregar parámetros de consulta si existen
    const params = new URLSearchParams();
    if (fechaInicio) params.append('fechaInicio', fechaInicio);
    if (fechaFin) params.append('fechaFin', fechaFin);
    if (limite) params.append('limite', limite.toString());
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const requestOptions: RequestInit = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      }
    };

    fetch(url, requestOptions)
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al obtener actividades de auditoría:", err);
        reject(err);
      });
  });
};

/**
 * Obtiene las actividades de auditoría de un usuario específico
 * 
 * @param token JWT de autenticación
 * @param userId ID del usuario
 * @param sucursal ID de la sucursal
 * @param seccion Sección del sistema (opcional)
 * @param fechaInicio Fecha de inicio para filtrar (opcional)
 * @param fechaFin Fecha de fin para filtrar (opcional)
 * @param limite Número máximo de registros a devolver (opcional)
 * @returns Promise con el resultado de la operación
 */
export const obtenerActividadesPorUsuario = (
  token: string,
  userId: string,
  sucursal: string,
  seccion?: string,
  fechaInicio?: string,
  fechaFin?: string,
  limite?: number
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    let url = `${apiUrl}/admin/auditoria/usuario/${userId}`;
    
    // Agregar parámetros de consulta
    const params = new URLSearchParams();
    params.append('sucursal', sucursal);
    if (seccion) params.append('seccion', seccion);
    if (fechaInicio) params.append('fechaInicio', fechaInicio);
    if (fechaFin) params.append('fechaFin', fechaFin);
    if (limite) params.append('limite', limite.toString());
    
    url += `?${params.toString()}`;
    
    const requestOptions: RequestInit = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      }
    };

    fetch(url, requestOptions)
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al obtener actividades de auditoría por usuario:", err);
        reject(err);
      });
  });
};
